/**
 * CR2 — Documents library (cr2b). A grid of all uploaded company documents with
 * a docType filter, an "Unggah" shortcut back to the upload flow, and an
 * AI-index summary banner driven by real /gcu counts. Clicking a card opens the
 * doc via a short-lived presigned URL.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Upload,
  Sparkles,
  ExternalLink,
  AlertTriangle,
  FileX2,
  Trash2,
  ArrowRight,
} from 'lucide-react';
import type { DocStatus, DocType, DocumentSummary } from '@klinik/shared';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { Toast } from '../components/ui/Toast';
import { useAuth } from '../lib/auth';
import { fadeOnly, fadeRise, staggerContainer } from '../lib/motion';
import {
  docTypeMeta,
  docStatusMeta,
  formatBytes,
  fileKindLabel,
  INTAKE_DOC_TYPES,
  isFullyIndexed,
} from '../lib/docs';
import { useDocuments, useGcu, openDocument, deleteDocument } from '../lib/useDocuments';
import { useToast } from '../lib/useToast';

const FILTERS: { key: DocType | 'all'; label: string }[] = [
  { key: 'all', label: 'Semua' },
  ...INTAKE_DOC_TYPES.map((t) => ({ key: t, label: docTypeMeta(t).tag })),
];

export function Documents() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const variants = reduce ? fadeOnly : fadeRise;
  const companyId = user?.companyId ?? null;

  const [filter, setFilter] = useState<DocType | 'all'>('all');
  const { documents, loading, error, reload, setDocuments } = useDocuments(companyId);
  const { gcu } = useGcu(companyId);
  const { toast, show, dismiss } = useToast();

  const visible = useMemo(
    () => (filter === 'all' ? documents : documents.filter((d) => d.docType === filter)),
    [documents, filter],
  );

  // AI-index roll-up from the GCU state (real counts, not the visible subset).
  const indexedCount = sumByStatuses(gcu?.documentCounts.byStatus, ['indexed']);
  const inPipelineCount = sumByStatuses(gcu?.documentCounts.byStatus, [
    'queued',
    'parsing',
    'parsed',
    'indexed',
  ]);

  async function onOpen(doc: DocumentSummary) {
    if (!companyId) return;
    const res = await openDocument(companyId, doc.id);
    if (!res.ok) show('error', res.message);
  }

  async function onDelete(doc: DocumentSummary) {
    if (!companyId) return;
    const prev = documents;
    setDocuments((p) => p.filter((d) => d.id !== doc.id));
    const res = await deleteDocument(companyId, doc.id);
    if (res.ok) show('success', `${doc.filename} dihapus.`);
    else {
      setDocuments(prev);
      show('error', res.message);
    }
  }

  return (
    <AppShell active="documents">
      <Toast toast={toast} onDismiss={dismiss} />
      <motion.div initial="hidden" animate="show" variants={staggerContainer}>
        {/* Header */}
        <motion.div variants={variants} className="mb-4 flex flex-wrap items-center gap-3">
          <h1 className="font-display text-xl font-bold text-slate-900">Dokumen Perusahaan</h1>
          <div className="ml-auto flex items-center gap-2">
            <Button
              onClick={() => navigate('/intake/upload')}
              icon={<Upload size={15} aria-hidden />}
            >
              Unggah
            </Button>
          </div>
        </motion.div>

        {/* Filter */}
        <motion.div variants={variants} className="mb-4 flex flex-wrap gap-1.5" role="tablist" aria-label="Filter dokumen">
          {FILTERS.map((f) => {
            const on = f.key === filter;
            const count =
              f.key === 'all'
                ? documents.length
                : documents.filter((d) => d.docType === f.key).length;
            return (
              <button
                key={f.key}
                role="tab"
                aria-selected={on}
                onClick={() => setFilter(f.key)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                  on
                    ? 'bg-brand text-white'
                    : 'border border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                }`}
              >
                {f.label}
                <span
                  className={`rounded-full px-1.5 text-[10px] ${
                    on ? 'bg-white/20' : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </motion.div>

        {/* Body */}
        {loading ? (
          <div className="h-64">
            <Spinner label="Memuat dokumen…" />
          </div>
        ) : error ? (
          <Card>
            <div className="flex items-start gap-3 text-sm text-red-700">
              <AlertTriangle size={18} className="mt-0.5 shrink-0" aria-hidden />
              <div className="flex-1">
                <div className="font-semibold">Gagal memuat dokumen</div>
                <div className="mt-0.5">{error}</div>
              </div>
              <Button variant="ghost" onClick={() => void reload()}>
                Coba lagi
              </Button>
            </div>
          </Card>
        ) : documents.length === 0 ? (
          <EmptyState onUpload={() => navigate('/intake/upload')} />
        ) : visible.length === 0 ? (
          <Card>
            <div className="py-8 text-center text-sm text-slate-400">
              Tidak ada dokumen pada kategori ini.
            </div>
          </Card>
        ) : (
          <>
            <motion.div
              variants={staggerContainer}
              className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            >
              {visible.map((doc) => (
                <DocCard
                  key={doc.id}
                  doc={doc}
                  variants={variants}
                  onOpen={() => void onOpen(doc)}
                  onDelete={() => void onDelete(doc)}
                />
              ))}
            </motion.div>

            {/* AI-index summary banner (real counts) */}
            <motion.div variants={variants} className="mt-4">
              <Card>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-violet-50 text-violet-600">
                    <Sparkles size={22} aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-800">
                      {indexedCount > 0
                        ? `AI telah mengindeks ${indexedCount} dokumen ke pgvector`
                        : inPipelineCount > 0
                          ? `${inPipelineCount} dokumen sedang diproses AI`
                          : 'Belum ada dokumen yang dianalisa AI'}
                    </div>
                    <div className="text-xs text-slate-400">
                      {indexedCount > 0
                        ? 'Retrieval per-perusahaan aktif. Siap menyusun kuesioner diagnosa.'
                        : 'Kirim dokumen ke analisa AI dari halaman upload untuk memulai.'}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/intake/upload')}
                    icon={<ArrowRight size={15} aria-hidden />}
                  >
                    Ke Upload
                  </Button>
                </div>
              </Card>
            </motion.div>
          </>
        )}
      </motion.div>
    </AppShell>
  );
}

function DocCard({
  doc,
  variants,
  onOpen,
  onDelete,
}: {
  doc: DocumentSummary;
  variants: typeof fadeRise;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const meta = docTypeMeta(doc.docType);
  const Icon = meta.icon;
  const status = docStatusMeta(doc.status);
  const indexed = isFullyIndexed(doc.status);

  return (
    <motion.div
      variants={variants}
      className="flex flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-soft"
    >
      <div className="flex items-start justify-between">
        <span
          className={`grid h-10 w-10 place-items-center rounded-xl ${
            indexed ? 'bg-violet-50 text-violet-600' : 'bg-blue-50 text-blue-600'
          }`}
        >
          <Icon size={20} aria-hidden />
        </span>
        <Badge tone={status.tone}>{status.label}</Badge>
      </div>

      <button
        onClick={onOpen}
        className="mt-3 line-clamp-2 text-left text-sm font-semibold leading-snug text-slate-800 hover:text-brand"
        title={`Buka ${doc.filename}`}
      >
        {doc.filename}
      </button>
      <div className="mt-1 text-[11px] text-slate-400">
        {fileKindLabel(doc.filename, doc.contentType)} · {formatBytes(doc.sizeBytes)}
      </div>
      {doc.status === 'failed' && doc.parseError && (
        <div className="mt-2 rounded-lg bg-red-50 px-2 py-1 text-[11px] text-red-600">
          {doc.parseError}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
        <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
          {meta.tag}
        </span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={onOpen}
            aria-label={`Buka ${doc.filename}`}
            className="grid h-7 w-7 place-items-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-brand"
          >
            <ExternalLink size={15} aria-hidden />
          </button>
          <button
            onClick={onDelete}
            aria-label={`Hapus ${doc.filename}`}
            className="grid h-7 w-7 place-items-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500"
          >
            <Trash2 size={15} aria-hidden />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function EmptyState({ onUpload }: { onUpload: () => void }) {
  return (
    <Card>
      <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
        <span className="grid h-14 w-14 place-items-center rounded-2xl bg-blue-50 text-blue-500">
          <FileX2 size={26} aria-hidden />
        </span>
        <div>
          <div className="font-display text-base font-bold text-slate-800">Belum ada dokumen</div>
          <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">
            Mulai General Check-Up dengan mengunggah data awal perusahaan (laporan keuangan, SOP,
            akad, dan lainnya).
          </p>
        </div>
        <Button onClick={onUpload} icon={<Upload size={15} aria-hidden />}>
          Unggah Dokumen
        </Button>
      </div>
    </Card>
  );
}

function sumByStatuses(
  byStatus: Partial<Record<DocStatus, number>> | undefined,
  statuses: DocStatus[],
): number {
  if (!byStatus) return 0;
  return statuses.reduce((sum, s) => sum + (byStatus[s] ?? 0), 0);
}
